/**
 * Particles.
 *
 * Flat typed arrays with a free-list, because the eat/bite bursts can put a
 * few thousand sparks on screen at once and allocation churn is the one thing
 * that will make a 60fps canvas game stutter.
 *
 * Coordinates are in *cells*, not pixels, so the whole system is resolution
 * independent and survives a window resize mid-run.
 */

const MAX = 4000

export type ParticleShape = 0 | 1 | 2 // dot, streak, ring

export class Particles {
  private x = new Float32Array(MAX)
  private y = new Float32Array(MAX)
  private vx = new Float32Array(MAX)
  private vy = new Float32Array(MAX)
  private life = new Float32Array(MAX)
  private maxLife = new Float32Array(MAX)
  private size = new Float32Array(MAX)
  private drag = new Float32Array(MAX)
  private r = new Uint8Array(MAX)
  private g = new Uint8Array(MAX)
  private b = new Uint8Array(MAX)
  private shape = new Uint8Array(MAX)
  private alive = new Uint8Array(MAX)
  private count = 0
  private cursor = 0

  get active(): number {
    return this.count
  }

  clear(): void {
    this.alive.fill(0)
    this.count = 0
    this.cursor = 0
  }

  spawn(
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    color: [number, number, number],
    shape: ParticleShape = 0,
    drag = 0.92,
  ): void {
    // Ring-buffer allocation: at saturation the oldest particle is recycled,
    // which is invisible in practice and keeps spawn() O(1) and allocation-free.
    let i = -1
    for (let n = 0; n < MAX; n++) {
      const c = (this.cursor + n) % MAX
      if (!this.alive[c]) {
        i = c
        break
      }
    }
    if (i < 0) i = this.cursor
    else this.count++
    this.cursor = (i + 1) % MAX

    this.alive[i] = 1
    this.x[i] = x
    this.y[i] = y
    this.vx[i] = vx
    this.vy[i] = vy
    this.life[i] = life
    this.maxLife[i] = life
    this.size[i] = size
    this.drag[i] = drag
    this.r[i] = color[0]
    this.g[i] = color[1]
    this.b[i] = color[2]
    this.shape[i] = shape
  }

  update(dt: number): void {
    for (let i = 0; i < MAX; i++) {
      if (!this.alive[i]) continue
      this.life[i] -= dt
      if (this.life[i] <= 0) {
        this.alive[i] = 0
        this.count--
        continue
      }
      const d = Math.pow(this.drag[i], dt * 60)
      this.vx[i] *= d
      this.vy[i] *= d
      this.x[i] += this.vx[i] * dt
      this.y[i] += this.vy[i] * dt
    }
  }

  draw(ctx: CanvasRenderingContext2D, cell: number): void {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (let i = 0; i < MAX; i++) {
      if (!this.alive[i]) continue
      const t = this.life[i] / this.maxLife[i]
      const a = t * t
      const px = this.x[i] * cell
      const py = this.y[i] * cell
      const s = this.size[i] * cell
      ctx.fillStyle = `rgba(${this.r[i]},${this.g[i]},${this.b[i]},${a})`

      if (this.shape[i] === 2) {
        ctx.strokeStyle = ctx.fillStyle
        ctx.lineWidth = Math.max(1, s * 0.25)
        ctx.beginPath()
        ctx.arc(px, py, s * (1.6 - t) * 2, 0, Math.PI * 2)
        ctx.stroke()
      } else if (this.shape[i] === 1) {
        ctx.strokeStyle = ctx.fillStyle
        ctx.lineCap = 'round'
        ctx.lineWidth = Math.max(1, s * t)
        ctx.beginPath()
        ctx.moveTo(px, py)
        ctx.lineTo(px - this.vx[i] * cell * 0.02, py - this.vy[i] * cell * 0.02)
        ctx.stroke()
      } else {
        ctx.beginPath()
        ctx.arc(px, py, Math.max(0.4, s * t), 0, Math.PI * 2)
        ctx.fill()
      }
    }
    ctx.restore()
  }

  // ── presets ────────────────────────────────────────────────────────────

  burst(
    x: number,
    y: number,
    n: number,
    color: [number, number, number],
    speed = 6,
    life = 0.6,
    rand: () => number = Math.random,
  ): void {
    for (let i = 0; i < n; i++) {
      const a = rand() * Math.PI * 2
      const v = speed * (0.35 + rand() * 0.9)
      this.spawn(
        x,
        y,
        Math.cos(a) * v,
        Math.sin(a) * v,
        life * (0.6 + rand() * 0.8),
        0.09 + rand() * 0.1,
        color,
        rand() < 0.35 ? 1 : 0,
      )
    }
  }

  ring(x: number, y: number, color: [number, number, number], life = 0.45): void {
    this.spawn(x, y, 0, 0, life, 0.32, color, 2, 1)
  }

  trail(x: number, y: number, color: [number, number, number], rand: () => number = Math.random): void {
    this.spawn(
      x,
      y,
      (rand() - 0.5) * 1.4,
      (rand() - 0.5) * 1.4,
      0.35 + rand() * 0.3,
      0.05 + rand() * 0.05,
      color,
      0,
      0.9,
    )
  }
}
